import { useDispatch } from 'react-redux';
import { useCallback, useState } from 'react';
import { trackEvent, deviceInfo, TABLET_LARGE_WIDTH } from '@surfline/web-common';
import encodeFormValues from '../../../../utils/encodeFormValues';
import { generateToken, authoriseAccessToken } from '../../../../api/user';
import { setupUser, getMeter, triggerModalRecompute } from '../../../../actions';
import { useUserSelector } from '../../../../selectors/user';
import { useMeterSelector } from '../../../../selectors/metering';
import {
  CLICKED_POPUP,
  CLICKED_SIGN_IN,
  FORCED_REGISTRATION,
  SIGNED_IN,
} from '../../../../common/constants';

export const useSignIn = () => {
  const dispatch = useDispatch();
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const user = useUserSelector();
  const meter = useMeterSelector();

  const { anonymousId } = meter || {};
  const { location, countryCode } = user;
  const { time_zone: timezone } = location || {};

  const signIn = useCallback(
    /**
     * @param {Object} values
     * @param {string} values.password
     * @param {string} values.email
     * @param {boolean?} values.staySignedIn
     * @return {Promise<void>}
     */
    async (values) => {
      try {
        trackEvent(CLICKED_POPUP, {
          action: CLICKED_SIGN_IN,
          category: FORCED_REGISTRATION,
        });

        setLoading(true);
        const { staySignedIn } = values;
        const { deviceId, deviceType } = deviceInfo();
        const encodedFormString = encodeFormValues({
          grant_type: 'password',
          username: values.email,
          password: values.password,
          forced: true,
          device_id: deviceId,
          device_type: deviceType,
        });

        const { accessToken } = await generateToken(!!staySignedIn, encodedFormString);
        const { id: userId } = await authoriseAccessToken(accessToken);

        window.analytics?.identify(userId);
        trackEvent(SIGNED_IN, {
          pageName: window?.location?.pathname,
          location: FORCED_REGISTRATION,
          platform: 'web',
          isMobileView: window?.innerWidth <= TABLET_LARGE_WIDTH,
        });

        await dispatch(setupUser({ accessToken }));
        await dispatch(
          getMeter({
            accessToken,
            createIfNotFound: true,
            countryCode,
            anonymousId,
            timezone,
            performMeterSurfCheck: true,
          }),
        );

        setLoading(false);
        dispatch(triggerModalRecompute());
      } catch (err) {
        setLoading(false);
        setError(err.message);
      }
    },
    [dispatch, timezone, countryCode, anonymousId],
  );
  return { signIn, loading, error };
};
